import React, { useRef, useState, useEffect, useMemo } from "react";
import { X, Printer, Calendar, History, RotateCcw, Lock, Unlock, Edit2, Download, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import api from "../../api/axios";
import authorisedSignature from "../../assets/authorised-signature.png";
import SalarySlipDocument from "./SalarySlipDocument";
import {
    getCurrentPayPeriod,
    getRecentPayPeriodOptions,
    getPayPeriodMonthBounds,
    getLastNPeriodsFromKey,
    listPeriodsBetweenKeys,
    filterExpensesInPeriod,
    computeSlipAmounts,
    computePeriodAttendanceStats,
    getAttendanceQueryBounds,
    getProratedSalaryForPeriod,
    formatPeriodRangeLabel,
    defaultCustomRangeKeys,
    clampCustomRangeKeys,
} from "../../utils/salarySlipUtils";

const RANGE_MODES = [
    { id: "single", label: "This month only" },
    { id: "3", label: "Last 3 months" },
    { id: "6", label: "Last 6 months" },
    { id: "custom", label: "Custom range" },
];

const SalarySlipModal = ({ open, onClose, employee }) => {
    const printAreaRef = useRef();
    const wasOpenRef = useRef(false);
    const [companySettings, setCompanySettings] = useState(null);
    const monthBounds = useMemo(() => getPayPeriodMonthBounds(36, companySettings?.payCycle), [companySettings?.payCycle]);
    const monthOptions = useMemo(() => getRecentPayPeriodOptions(36, companySettings?.payCycle), [companySettings?.payCycle]);
    const defaultKey = getCurrentPayPeriod(companySettings?.payCycle).key;
    const currentKey = defaultKey;

    const [approvedExpenses, setApprovedExpenses] = useState([]);
    const [selectedMonthKey, setSelectedMonthKey] = useState(defaultKey);
    const [rangeMode, setRangeMode] = useState("single");
    const [customFromKey, setCustomFromKey] = useState(defaultKey);
    const [customToKey, setCustomToKey] = useState(defaultKey);
    const [savedSlips, setSavedSlips] = useState([]);
    const [salaryHistory, setSalaryHistory] = useState([]);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [leaveRecords, setLeaveRecords] = useState([]);
    const [latestEmployee, setLatestEmployee] = useState(null);
    const [activeSavedSlips, setActiveSavedSlips] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [savingHistory, setSavingHistory] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [lockUpdatingId, setLockUpdatingId] = useState("");
    const [editSection, setEditSection] = useState(null);
    const [manualOverrides, setManualOverrides] = useState({});
    const [slipConfig, setSlipConfig] = useState({
        showEmployeeDetails: true,
        showPeriodDetails: true,
        showPaymentDetails: true,
        showEarnings: true,
        showDeductions: true,
        showAttendanceSummary: true,
        showPaymentInfo: true,
    });
    
    const [payrollConfig, setPayrollConfig] = useState({
        useBasic: false, basicPercent: 50,
        useHRA: false, hraPercent: 40,
        useDA: false, daPercent: 0,
        usePF: false, pfPercent: 12, pfEmployerPercent: 12,
        useESI: false, esiPercent: 0.75, esiEmployerPercent: 3.25,
        useProfTax: false, profTaxAmount: 0,
        useOvertime: false, otHours: 0, otRate: 0,
        useBonus: false, bonusPercent: 0,
        useGratuity: false, gratuityYears: 0,
        useStayAllowance: false, stayAllowanceAmount: 0,
    });

    const slipEmployee = latestEmployee?._id === employee?._id ? latestEmployee : employee;

    useEffect(() => {
        if (slipEmployee?.payrollConfig) {
            setPayrollConfig((prev) => ({ ...prev, ...slipEmployee.payrollConfig }));
        }
    }, [slipEmployee?.payrollConfig]);

    useEffect(() => {
        if (open && employee?._id) {
            api.get(`/api/admin/employees/${employee._id}`, { params: { _t: Date.now() } })
                .then((res) => setLatestEmployee(res.data || null))
                .catch(() => setLatestEmployee(null));
            api.get("/api/settings").then(res => setCompanySettings(res.data)).catch(console.error);
            setHistoryLoading(true);
            api.get(`/api/admin/expenses/employee/${employee._id}`)
                .then((res) => setApprovedExpenses(res.data.filter((e) => e.status === "Approved")))
                .catch(() => setApprovedExpenses([]));
            api.get(`/api/admin/salary-slips/${employee._id}`)
                .then((res) => setSavedSlips(Array.isArray(res.data) ? res.data : []))
                .catch(() => setSavedSlips([]))
                .finally(() => setHistoryLoading(false));
            api.get(`/api/admin/salary-history/${employee._id}`)
                .then((res) => setSalaryHistory(Array.isArray(res.data) ? res.data : []))
                .catch(() => setSalaryHistory([]));
        } else {
            setLatestEmployee(null);
            setApprovedExpenses([]);
            setSavedSlips([]);
            setSalaryHistory([]);
            setAttendanceRecords([]);
            setLeaveRecords([]);
            setActiveSavedSlips(null);
        }
    }, [open, employee?._id]);

    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setSelectedMonthKey(defaultKey);
            setRangeMode("single");
            const { fromKey, toKey } = defaultCustomRangeKeys(defaultKey, 3, monthBounds);
            setCustomFromKey(fromKey);
            setCustomToKey(toKey);
        }
        wasOpenRef.current = open;
    }, [open, defaultKey, monthBounds]);

    const handleRangeModeChange = (mode) => {
        setActiveSavedSlips(null);
        setRangeMode(mode);
        if (mode === "custom") {
            const { fromKey, toKey } = defaultCustomRangeKeys(
                selectedMonthKey || defaultKey,
                3,
                monthBounds,
            );
            setCustomFromKey(fromKey);
            setCustomToKey(toKey);
        }
    };

    const handleCustomFromChange = (fromKey) => {
        setActiveSavedSlips(null);
        const { fromKey: from, toKey: to } = clampCustomRangeKeys(fromKey, customToKey, monthBounds);
        setCustomFromKey(from);
        setCustomToKey(to);
    };

    const handleCustomToChange = (toKey) => {
        setActiveSavedSlips(null);
        const { fromKey: from, toKey: to } = clampCustomRangeKeys(customFromKey, toKey, monthBounds);
        setCustomFromKey(from);
        setCustomToKey(to);
    };

    const periods = useMemo(() => {
        if (rangeMode === "single") {
            return getLastNPeriodsFromKey(selectedMonthKey, 1);
        }
        if (rangeMode === "3") {
            return getLastNPeriodsFromKey(selectedMonthKey, 3);
        }
        if (rangeMode === "6") {
            return getLastNPeriodsFromKey(selectedMonthKey, 6);
        }
        return listPeriodsBetweenKeys(customFromKey, customToKey);
    }, [rangeMode, selectedMonthKey, customFromKey, customToKey]);

    useEffect(() => {
        if (!open || !employee?._id || !periods.length) return;

        const bounds = getAttendanceQueryBounds(periods);
        if (!bounds?.startDate || !bounds?.endDate) return;

        let cancelled = false;
        Promise.all([
            api.get("/api/admin/attendance", {
                params: {
                    employee: employee._id,
                    startDate: bounds.startDate,
                    endDate: bounds.endDate,
                    _t: Date.now(),
                },
            }),
            api.get("/api/admin/leave", {
                params: {
                    employee: employee._id,
                    limit: 500,
                    _t: Date.now(),
                },
            }),
        ])
            .then(([attendanceRes, leaveRes]) => {
                if (cancelled) return;
                setAttendanceRecords(Array.isArray(attendanceRes.data) ? attendanceRes.data : []);
                setLeaveRecords(Array.isArray(leaveRes.data) ? leaveRes.data : []);
            })
            .catch(() => {
                if (cancelled) return;
                setAttendanceRecords([]);
                setLeaveRecords([]);
            });

        return () => {
            cancelled = true;
        };
    }, [open, employee?._id, periods]);

    const slipsData = useMemo(
        () =>
            periods.map((period) => {
                const saved = savedSlips.find((s) => s.periodKey === period.key);
                if (saved && saved.locked !== false && saved.snapshot) {
                    return saved.snapshot;
                }

                const periodExpenses = filterExpensesInPeriod(
                    approvedExpenses,
                    period.periodStart,
                    period.periodEnd,
                );
                const proratedSalary = getProratedSalaryForPeriod(slipEmployee, salaryHistory, period);
                const fullMonthlySalary = Number(slipEmployee?.salary || 0);
                const salaryEmployee = { ...slipEmployee, salary: fullMonthlySalary };
                const attendanceStats = computePeriodAttendanceStats(salaryEmployee, period, {
                    attendance: attendanceRecords,
                    leaves: leaveRecords,
                });
                const amounts = computeSlipAmounts(
                    salaryEmployee,
                    { ...period, grossOverride: fullMonthlySalary, payrollConfig },
                    periodExpenses,
                    period.key === currentKey,
                    attendanceStats,
                );
                const basic = Number(amounts.basic) || 0;
                const daAmount = Number(amounts.daAmount) || 0;
                const hraAmount = Number(amounts.hraAmount) || 0;
                const otherAllowancesAmount = Number(amounts.otherAllowancesAmount) || 0;
                const otAmount = Number(amounts.otAmount) || 0;
                const bonusAmount = Number(amounts.bonusAmount) || 0;
                
                const pfAmount = Number(amounts.pfAmount) || 0;
                const esiAmount = Number(amounts.esiAmount) || 0;
                const profTaxAmount = Number(amounts.profTaxAmount) || 0;
                const lopDed = Number(amounts.lopDed) || 0;
                const stayAllowance = Number(amounts.stayAllowance) || 0;
                
                const totalExpenses = amounts.totalExpenses || 0;

                amounts.gross = basic + daAmount + hraAmount + otherAllowancesAmount + otAmount + bonusAmount;
                amounts.totalDed = pfAmount + esiAmount + profTaxAmount + lopDed + stayAllowance;
                amounts.net = amounts.gross + totalExpenses - amounts.totalDed;

                return {
                    period: { ...period, salarySegments: proratedSalary.segments, ...amounts, slipConfig },
                    expenses: periodExpenses,
                    employee: salaryEmployee,
                };
            }),
        [periods, approvedExpenses, slipEmployee, salaryHistory, currentKey, attendanceRecords, leaveRecords, payrollConfig, slipConfig, savedSlips],
    );

    const displaySlipsData = activeSavedSlips || slipsData;

    if (!open || !employee) return null;

    const signatureSrc =
        typeof authorisedSignature === "string" && /^https?:\/\//i.test(authorisedSignature)
            ? authorisedSignature
            : `${window.location.origin}${authorisedSignature}`;

    const displayPeriods = displaySlipsData.map(({ period }) => period);
    const rangeLabel = formatPeriodRangeLabel(displayPeriods);
    const slipCount = displaySlipsData.length;

    const handleMonthChange = (value) => {
        setActiveSavedSlips(null);
        setSelectedMonthKey(value);
    };

    const saveGeneratedSlips = async () => {
        if (!employee?._id || activeSavedSlips) return savedSlips;
        setSavingHistory(true);
        try {
            const slipsToSave = slipsData.filter(({ period }) => {
                const saved = savedSlips.find((s) => s.periodKey === period.key);
                return !saved || saved.locked === false;
            });

            if (slipsToSave.length > 0) {
                const res = await api.post(`/api/admin/salary-slips/${employee._id}`, {
                    slips: slipsToSave.map(({ period, expenses }) => ({
                        period: {
                            ...period,
                            payrollConfig,
                            slipConfig,
                            manualOverrides,
                        },
                        expenses,
                        manualOverrides,
                    })),
                });
                const next = Array.isArray(res.data) ? res.data : [];
                setSavedSlips((current) => {
                    const byKey = new Map(current.map((item) => [item.periodKey, item]));
                    next.forEach((item) => byKey.set(item.periodKey, item));
                    return Array.from(byKey.values()).sort((a, b) => String(b.periodKey).localeCompare(String(a.periodKey)));
                });
                if (next.length === 1 && next[0]?.snapshot) {
                    setActiveSavedSlips([{
                        period: next[0].snapshot.period,
                        expenses: next[0].snapshot.expenses || [],
                        employee: next[0].snapshot.employee,
                    }]);
                }
                return next;
            }
            return savedSlips;
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to generate and lock slips");
            throw err;
        } finally {
            setSavingHistory(false);
        }
    };

    const updateSavedSlipLock = async (slip, locked) => {
        setLockUpdatingId(slip._id);
        try {
            const res = await api.patch(`/api/admin/salary-slips/${slip._id}/lock`, { locked });
            setSavedSlips((current) =>
                current.map((item) => item._id === slip._id ? res.data : item),
            );
        } finally {
            setLockUpdatingId("");
        }
    };

    const loadSavedSlip = (slip) => {
        if (!slip?.snapshot) return;
        setActiveSavedSlips([{
            period: slip.snapshot.period,
            expenses: slip.snapshot.expenses || [],
            employee: slip.snapshot.employee,
            locked: slip.locked !== false
        }]);
        if (slip.snapshot.period?.payrollConfig) {
            setPayrollConfig({ ...payrollConfig, ...slip.snapshot.period.payrollConfig });
        }
        if (slip.snapshot.period?.slipConfig) {
            setSlipConfig({ ...slipConfig, ...slip.snapshot.period.slipConfig });
        }
    };
    
    const handleEditUnlocked = () => {
        if (!activeSavedSlips || !activeSavedSlips[0]) return;
        const slipPeriod = activeSavedSlips[0].period;
        setSelectedMonthKey(slipPeriod.key);
        setRangeMode("single");
        setActiveSavedSlips(null);
    };

    const handlePrint = async () => {
        try {
            await saveGeneratedSlips();
        } catch (err) {
            return; // Stop execution, the error is already toasted
        }

        const pw = window.open("", "_blank", "height=900,width=1050");
        const title =
            slipCount > 1
                ? `Salary Slips – ${employee.name} – ${rangeLabel}`
                : `Salary Slip – ${employee.name} – ${displayPeriods[0]?.payMonth || ""}`;

        pw.document.write(`<html><head><title>${title}</title>
        <style>
            *{box-sizing:border-box;margin:0;padding:0}
            body{font-family:'Plus Jakarta Sans',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:11px;color:#111;background:#fff;padding:20px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
            .slip{max-width:860px;margin:0 auto}
            .slip-page{page-break-after:always;break-after:page}
            .slip-page:last-child{page-break-after:auto;break-after:auto}
        </style></head><body><div class="slip">`);
        pw.document.write(printAreaRef.current.innerHTML);
        pw.document.write("</div></body></html>");
        pw.document.close();
        setTimeout(() => {
            pw.focus();
            pw.print();
            pw.close();
        }, 400);
    };

    const handleDownloadPdf = async () => {
        if (!printAreaRef.current) return;
        setDownloadingPdf(true);
        const toastId = toast.loading("Generating PDF download...");
        try {
            await saveGeneratedSlips();

            const cleanName = (employee.name || "Employee").replace(/[^a-zA-Z0-9_-]/g, "_");
            const filename =
                slipCount > 1
                    ? `Salary_Slips_${cleanName}_${rangeLabel.replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`
                    : `Salary_Slip_${cleanName}_${(displayPeriods[0]?.payMonth || "Salary").replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`;

            const slipElements = Array.from(printAreaRef.current.children);
            const elementsToRender = slipElements.length > 0 ? slipElements : [printAreaRef.current];

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
            });
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            for (let i = 0; i < elementsToRender.length; i++) {
                const el = elementsToRender[i];
                const canvas = await html2canvas(el, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: "#ffffff",
                });
                const imgData = canvas.toDataURL("image/png");
                const imgWidth = pdfWidth;
                const imgHeight = (canvas.height * pdfWidth) / canvas.width;

                if (i > 0) {
                    pdf.addPage();
                }

                if (imgHeight > pdfHeight) {
                    const scaledWidth = (canvas.width * pdfHeight) / canvas.height;
                    const marginX = (pdfWidth - scaledWidth) / 2;
                    pdf.addImage(imgData, "PNG", marginX, 0, scaledWidth, pdfHeight);
                } else {
                    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
                }
            }

            pdf.save(filename);
            toast.success("Salary slip downloaded successfully as PDF!", { id: toastId });
        } catch (err) {
            console.error("PDF download error:", err);
            toast.error("Failed to generate PDF download", { id: toastId });
        } finally {
            setDownloadingPdf(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex justify-center items-start px-2 py-6 overflow-y-auto">
            <div onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-4xl bg-[var(--card-bg)] shadow-2xl z-10 mt-2 mb-10"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="flex flex-col gap-3 px-5 py-3 border-b border-gray-200 bg-[var(--bg-secondary)] relative z-20">
                    <div className="flex justify-end items-center">
                        <div className="hidden">
                            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Salary Slip</h2>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                                {slipCount > 1
                                    ? `${slipCount} slips · ${rangeLabel}`
                                    : "Select pay period · Print or save as PDF"}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            {activeSavedSlips && (
                                <>
                                    {activeSavedSlips[0]?.locked === false && (
                                        <button
                                            type="button"
                                            onClick={handleEditUnlocked}
                                            className="inline-flex items-center gap-1.5 px-3 py-2 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold text-xs transition-all"
                                        >
                                            <RotateCcw size={13} />
                                            Edit Unlocked Slip
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setRangeMode("month");
                                            setActiveSavedSlips(null);
                                        }}
                                        className="px-4 py-2 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] shadow-sm text-white rounded font-medium shadow-sm transition-colors"
                                    >
                                        Back to Live Preview
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={handleDownloadPdf}
                                disabled={savingHistory || downloadingPdf}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs transition-all shadow-sm rounded-sm"
                            >
                                {downloadingPdf ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                                {downloadingPdf ? "Downloading..." : slipCount > 1 ? `Download PDF (${slipCount})` : "Download PDF"}
                            </button>
                            <button
                                type="button"
                                onClick={handlePrint}
                                disabled={savingHistory || downloadingPdf}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white font-semibold text-xs transition-all shadow-sm rounded-sm"
                            >
                                <Printer size={13} />
                                {savingHistory ? "Generating & locking..." : slipCount > 1 ? `Generate, lock & print ${slipCount}` : "Generate, Lock & Print"}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-9 h-9 flex items-center justify-center border border-gray-300 hover:bg-gray-100 text-gray-500 transition-all rounded-sm"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 pt-1 border-t border-gray-200/80">
                        <div className="flex flex-wrap items-end gap-3">
                            {rangeMode !== "custom" && (
                                <div className="flex flex-col gap-1 min-w-[180px]">
                                    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                        <Calendar size={11} /> Pay month (end month)
                                    </label>
                                    <select
                                        value={selectedMonthKey}
                                        onChange={(e) => handleMonthChange(e.target.value)}
                                        className="text-xs border border-gray-300 rounded-lg px-3 py-2 bg-[var(--card-bg)] text-gray-800 focus:border-[var(--accent-primary)] outline-none cursor-pointer"
                                    >
                                        {monthOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                                    Download period
                                </label>
                                <div className="flex flex-wrap gap-1.5">
                                    {RANGE_MODES.map((m) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => handleRangeModeChange(m.id)}
                                            className={`text-[11px] px-3 py-1.5 rounded-lg border font-medium transition-all ${
                                                rangeMode === m.id
                                                    ? "bg-[var(--accent-primary)] text-white border-[var(--accent-primary)]"
                                                    : "bg-[var(--card-bg)] text-gray-600 border-gray-300 hover:border-[var(--accent-primary)]"
                                            }`}
                                        >
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {rangeMode === "custom" && (
                            <div className="w-full flex flex-col sm:flex-row gap-3 p-3 rounded-xl bg-[var(--card-bg)] border border-[var(--accent-primary)]/20">
                                <p className="text-[10px] text-gray-500 w-full sm:hidden">
                                    Pick any months (up to 36 months back). Each month generates one salary slip.
                                </p>
                                <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
                                    <label htmlFor="slip-from-month" className="text-[10px] font-semibold text-gray-600">
                                        From (start month)
                                    </label>
                                    <input
                                        id="slip-from-month"
                                        type="month"
                                        min={monthBounds.min}
                                        max={monthBounds.max}
                                        value={customFromKey}
                                        onChange={(e) => handleCustomFromChange(e.target.value)}
                                        className="text-sm border border-gray-300 rounded-lg px-3 py-2.5 bg-[var(--card-bg)] text-gray-800 focus:border-[var(--accent-primary)] outline-none cursor-pointer w-full"
                                    />
                                </div>
                                <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
                                    <label htmlFor="slip-to-month" className="text-[10px] font-semibold text-gray-600">
                                        To (end month)
                                    </label>
                                    <input
                                        id="slip-to-month"
                                        type="month"
                                        min={monthBounds.min}
                                        max={monthBounds.max}
                                        value={customToKey}
                                        onChange={(e) => handleCustomToChange(e.target.value)}
                                        className="text-sm border border-gray-300 rounded-lg px-3 py-2.5 bg-[var(--card-bg)] text-gray-800 focus:border-[var(--accent-primary)] outline-none cursor-pointer w-full"
                                    />
                                </div>
                                <p className="hidden sm:flex items-end text-[10px] text-gray-500 pb-2 flex-1">
                                    {slipCount} slip{slipCount !== 1 ? "s" : ""} · {formatPeriodRangeLabel(periods)}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="pt-1 border-t border-gray-200/80">
                        <div className="flex items-center gap-2 mb-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                            <History size={12} /> Saved History
                        </div>
                        {historyLoading ? (
                            <p className="text-xs text-gray-400">Loading saved salary slips...</p>
                        ) : savedSlips.length === 0 ? (
                            <p className="text-xs text-gray-400">No saved salary slips yet. Download once to save this month.</p>
                        ) : (
                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {savedSlips.map((slip) => (
                                    <div
                                        key={slip._id || slip.periodKey}
                                        className="shrink-0 rounded-lg border border-gray-300 bg-[var(--card-bg)] p-2 hover:border-[var(--accent-primary)] transition-all"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => loadSavedSlip(slip)}
                                            className="block text-left"
                                        >
                                            <span className="flex items-center gap-1 text-xs font-bold text-gray-800">
                                                {slip.locked !== false ? <Lock size={11} /> : <Unlock size={11} />}
                                                {slip.payMonth}
                                            </span>
                                            <span className="block text-[10px] text-gray-400">
                                                Saved {new Date(slip.updatedAt || slip.createdAt).toLocaleDateString("en-IN")}
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            disabled={lockUpdatingId === slip._id}
                                            onClick={() => updateSavedSlipLock(slip, slip.locked === false)}
                                            className={`mt-2 w-full rounded-md px-2 py-1 text-[10px] font-bold transition-all ${
                                                slip.locked !== false
                                                    ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                                    : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                            }`}
                                        >
                                            {lockUpdatingId === slip._id ? "Updating..." : slip.locked !== false ? "Unlock" : "Lock"}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {(!activeSavedSlips || activeSavedSlips[0]?.locked === false) && (
                        <div className="pt-2 pb-1 border-t border-gray-200/80">
                            <h3 className="text-xs font-semibold uppercase text-gray-500 mb-3 px-1">Payroll Configuration</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {[
                                    { id: 'useBasic', label: 'Basic Salary', dep: 'basicPercent', depLabel: '% of Gross' },
                                    { id: 'useHRA', label: 'HRA', dep: 'hraPercent', depLabel: '% of Basic' },
                                    { id: 'useDA', label: 'DA', dep: 'daPercent', depLabel: '% of Basic' },
                                    { id: 'usePF', label: 'PF (Employee)', dep: 'pfPercent', depLabel: '% of Basic' },
                                    { id: 'useESI', label: 'ESI (Employee)', dep: 'esiPercent', depLabel: '% of Gross' },
                                    { id: 'useProfTax', label: 'Professional Tax', dep: 'profTaxAmount', depLabel: 'Amount (₹)' },
                                    { id: 'useOvertime', label: 'Overtime', dep: 'otHours', depLabel: 'OT Hours', dep2: 'otRate', dep2Label: 'OT Rate (₹)' },
                                    { id: 'useBonus', label: 'Bonus', dep: 'bonusPercent', depLabel: '% of Gross' },
                                    { id: 'useGratuity', label: 'Gratuity', dep: 'gratuityYears', depLabel: 'Years' },
                                    { id: 'useStayAllowance', label: 'Stay Allowance', dep: 'stayAllowanceAmount', depLabel: 'Amount (₹)' }
                                ].map((cfg) => (
                                    <div key={cfg.id} className="flex flex-col gap-1 p-2 border border-gray-200 rounded-lg bg-[var(--card-bg)]">
                                        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={payrollConfig[cfg.id]} 
                                                onChange={(e) => setPayrollConfig({ ...payrollConfig, [cfg.id]: e.target.checked })}
                                                className="w-3 h-3 text-[#1e3a8a] rounded border-gray-300 focus:ring-[#1e3a8a]"
                                            />
                                            {cfg.label}
                                        </label>
                                        {payrollConfig[cfg.id] && (
                                            <div className="flex gap-1 mt-1">
                                                <div className="flex flex-col flex-1">
                                                    <span className="text-[9px] text-gray-500">{cfg.depLabel}</span>
                                                    <input 
                                                        type="number" 
                                                        value={payrollConfig[cfg.dep]} 
                                                        onChange={(e) => setPayrollConfig({ ...payrollConfig, [cfg.dep]: e.target.value })}
                                                        className="w-full text-[10px] border border-gray-300 rounded px-1.5 py-1"
                                                    />
                                                </div>
                                                {cfg.dep2 && (
                                                    <div className="flex flex-col flex-1">
                                                        <span className="text-[9px] text-gray-500">{cfg.dep2Label}</span>
                                                        <input 
                                                            type="number" 
                                                            value={payrollConfig[cfg.dep2]} 
                                                            onChange={(e) => setPayrollConfig({ ...payrollConfig, [cfg.dep2]: e.target.value })}
                                                            className="w-full text-[10px] border border-gray-300 rounded px-1.5 py-1"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <h3 className="text-xs font-semibold uppercase text-gray-500 mb-3 mt-5 px-1">Slip Layout Configuration</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                                {[
                                    { id: 'showEmployeeDetails', label: 'Employee Details', editKey: 'employee' },
                                    { id: 'showPeriodDetails', label: 'Salary Period Details', editKey: 'period' },
                                    { id: 'showPaymentDetails', label: 'Payment Details', editKey: 'payment' },
                                    { id: 'showEarnings', label: 'Earnings Section' },
                                    { id: 'showDeductions', label: 'Deductions Section' },
                                    { id: 'showAttendanceSummary', label: 'Attendance Summary' },
                                    { id: 'showPaymentInfo', label: 'Payment Info Footer' },
                                ].map((conf) => (
                                    <div key={conf.id} className={`flex items-center justify-between gap-2 p-2 rounded border transition-colors ${editSection === conf.editKey ? 'border-blue-400 bg-[var(--accent-light)]' : 'bg-[var(--bg-secondary)] border-gray-200 hover:bg-gray-100'}`}>
                                        <label className="flex items-center gap-2 cursor-pointer flex-1">
                                            <input
                                                type="checkbox"
                                                checked={slipConfig[conf.id]}
                                                onChange={(e) => setSlipConfig({ ...slipConfig, [conf.id]: e.target.checked })}
                                                className="rounded text-[var(--accent-primary)] focus:ring-blue-500 w-4 h-4 cursor-pointer"
                                            />
                                            <span className="text-xs font-medium text-gray-700">{conf.label}</span>
                                        </label>
                                        {conf.editKey && slipConfig[conf.id] && (
                                            <button 
                                                onClick={() => setEditSection(editSection === conf.editKey ? null : conf.editKey)}
                                                className="text-gray-400 hover:text-[var(--accent-primary)]"
                                                title="Edit Details"
                                            >
                                                <Edit2 size={13} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {editSection && (
                                <div className="mt-2 mb-4 p-3 border border-[var(--accent-soft)] bg-[var(--accent-light)]/50 rounded-lg">
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="text-[11px] font-bold text-blue-800 uppercase tracking-wide">
                                            Editing {editSection === 'employee' ? 'Employee Details' : editSection === 'period' ? 'Salary Period Details' : 'Payment Details'}
                                        </h4>
                                        <button onClick={() => setEditSection(null)} className="text-gray-500 hover:text-gray-800"><X size={14} /></button>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        {editSection === 'employee' && [
                                            { key: 'employeeName', label: 'Employee Name' },
                                            { key: 'employeeId', label: 'Employee ID' },
                                            { key: 'designation', label: 'Designation' },
                                            { key: 'department', label: 'Department' },
                                            { key: 'panNumber', label: 'PAN Number' },
                                            { key: 'uanNumber', label: 'UAN Number' }
                                        ].map(field => (
                                            <div key={field.key} className="flex flex-col gap-1">
                                                <label className="text-[10px] text-gray-600 font-medium">{field.label}</label>
                                                <input 
                                                    type="text"
                                                    value={manualOverrides[field.key] || ''}
                                                    onChange={(e) => setManualOverrides({ ...manualOverrides, [field.key]: e.target.value })}
                                                    className="text-[11px] px-2 py-1.5 border border-gray-300 rounded focus:border-blue-500 outline-none"
                                                    placeholder="Default"
                                                />
                                            </div>
                                        ))}

                                        {editSection === 'period' && [
                                            { key: 'payMonth', label: 'Pay Month' },
                                            { key: 'payPeriod', label: 'Pay Period' },
                                            { key: 'workDays', label: 'Working Days' },
                                            { key: 'paidDays', label: 'Paid Days' },
                                            { key: 'leaveTakenDays', label: 'Leave Taken' },
                                            { key: 'lopDays', label: 'Loss of Pay (LOP)' }
                                        ].map(field => (
                                            <div key={field.key} className="flex flex-col gap-1">
                                                <label className="text-[10px] text-gray-600 font-medium">{field.label}</label>
                                                <input 
                                                    type="text"
                                                    value={manualOverrides[field.key] || ''}
                                                    onChange={(e) => setManualOverrides({ ...manualOverrides, [field.key]: e.target.value })}
                                                    className="text-[11px] px-2 py-1.5 border border-gray-300 rounded focus:border-blue-500 outline-none"
                                                    placeholder="Default"
                                                />
                                            </div>
                                        ))}

                                        {editSection === 'payment' && [
                                            { key: 'paymentMode', label: 'Payment Mode' },
                                            { key: 'bankName', label: 'Bank Name' },
                                            { key: 'accountNo', label: 'Account No.' },
                                            { key: 'ifscCode', label: 'IFSC Code' },
                                            { key: 'payDate', label: 'Payment Date' }
                                        ].map(field => (
                                            <div key={field.key} className="flex flex-col gap-1">
                                                <label className="text-[10px] text-gray-600 font-medium">{field.label}</label>
                                                <input 
                                                    type="text"
                                                    value={manualOverrides[field.key] || ''}
                                                    onChange={(e) => setManualOverrides({ ...manualOverrides, [field.key]: e.target.value })}
                                                    className="text-[11px] px-2 py-1.5 border border-gray-300 rounded focus:border-blue-500 outline-none"
                                                    placeholder="Default"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="overflow-auto bg-gray-100 p-4 max-h-[60vh]">
                    <div ref={printAreaRef}>
                        {displaySlipsData.map(({ period, expenses, employee: savedEmployee, locked }, idx) => {
                            return (
                                <SalarySlipDocument companySettings={companySettings?.salarySlip}
                                    key={period.key}
                                    employee={savedEmployee || slipEmployee}
                                    period={period}
                                    approvedExpenses={expenses}
                                    signatureSrc={signatureSrc}
                                    pageBreakAfter={idx < slipsData.length - 1}
                                    slipConfig={period.slipConfig || slipConfig}
                                    manualOverrides={manualOverrides}
                                />
                            );
                        })}
                    </div>
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-200 bg-[var(--bg-secondary)] px-5 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 bg-[var(--card-bg)] px-4 py-2 text-xs font-semibold text-gray-600 transition-all hover:bg-gray-100"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SalarySlipModal;
